"use client";

import type { StorefrontExternalBookingDto } from "@tripcart/shared";
import { useId } from "react";

import { ExternalBookButton } from "@/components/external-book-button";
import { useFormatMoney } from "@/lib/currency-format-context";
import { hasDisplayPrice } from "@/lib/price-display";
import { useTranslator } from "@/lib/translation-context";

import { PriceFigure } from "../_price-figure";
import styles from "./external-booking-rail.module.css";

interface ExternalBookingRailProps {
  booking: StorefrontExternalBookingDto;
  priceFrom?: { amount: number; currencyCode: string } | null;
  priceSuffix?: string;
}

/** What the booking rail shows for a tour that books on the agency's own site:
 *  the headline price, then the link to the booking page or the operator's
 *  widget HTML in the Book button's place. */
export function ExternalBookingRail({
  booking,
  priceFrom,
  priceSuffix,
}: ExternalBookingRailProps) {
  const t = useTranslator();
  const formatMoney = useFormatMoney();
  const noteId = useId();
  const newTab = booking.type === "link" && booking.openInNewTab;
  return (
    <div className={styles.card}>
      {hasDisplayPrice(priceFrom) ? (
        <div className={styles.price}>
          <span className={styles.priceLabel}>{t("From")}</span>
          <PriceFigure
            formatted={formatMoney(priceFrom.amount)}
            className={styles.amount}
            affixClassName={styles.amountAffix}
          />
          {priceSuffix ? (
            <span className={styles.suffix}>{priceSuffix}</span>
          ) : null}
        </div>
      ) : null}
      <ExternalBookButton
        booking={booking}
        className={styles.cta}
        describedBy={newTab ? noteId : undefined}
      />
      {newTab ? (
        <p id={noteId} className={styles.note}>
          {t("Opens your booking page in a new tab")}
        </p>
      ) : null}
    </div>
  );
}
